import { Badge } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import DppModal from './DppModal';
import getLocalizedText from '../Utils';
import Config from '../../config/Config';

const DetailItem = ({ label, value, code = false }) => (
  <div className="mb-2">
    <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#999', marginBottom: '2px' }}>
      {label}
    </div>
    {code ? (
      <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '5px 10px', marginTop: '2px' }}>
        <code style={{ fontSize: '12px', color: '#555', wordBreak: 'break-all' }}>{value}</code>
      </div>
    ) : (
      <div style={{ fontSize: '14px', color: '#333', wordBreak: 'break-word' }}>{value}</div>
    )}
  </div>
);

const SectionLabel = ({ children }) => (
  <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#999', borderBottom: '1px solid #f0f0f0', paddingBottom: '5px', marginBottom: '10px' }}>
    {children}
  </div>
);

const AasDetailsModal = ({ show, handleClose, assetInfo }) => {
  const intl = useIntl();
  const { mainColor } = Config.appearance;

  const {
    assetInformation,
    description,
    displayName,
    embeddedDataSpecifications = [],
    extensions = [],
    id,
    idShort,
  } = assetInfo || {};

  const specificAssetIds = assetInformation?.specificAssetIds ?? [];

  const t = (key, def) => intl.formatMessage({ id: key, defaultMessage: def });

  const displayNameLocalized = getLocalizedText(displayName, null, intl.locale);
  const descriptionLocalized = getLocalizedText(description, null, intl.locale);

  return (
    <DppModal
      show={show}
      handleClose={handleClose}
      size="lg"
      centered
      title={t('asset_information', 'Asset Information')}
    >
      <div>

        {/* Identifiers */}
        <div className="row g-0">
          {id && (
            <div className="col-12 mb-2">
              <DetailItem label={t('aas_id', 'AAS ID')} value={id} code />
            </div>
          )}
          {idShort && (
            <div className="col-12 col-sm-6 mb-2 pe-sm-3">
              <DetailItem label={t('id_short', 'ID Short')} value={idShort} />
            </div>
          )}
          {displayNameLocalized && (
            <div className="col-12 col-sm-6 mb-2">
              <DetailItem label={t('display_name', 'Display Name')} value={displayNameLocalized} />
            </div>
          )}
        </div>

        {descriptionLocalized && (
          <DetailItem label={t('description', 'Description')} value={descriptionLocalized} />
        )}

        {/* Asset Information */}
        {assetInformation && (
          <>
            <hr style={{ borderColor: '#f0f0f0', margin: '4px 0 12px' }} />

            <div className="row g-0">
              {assetInformation.globalAssetId && (
                <div className="col-12 mb-2">
                  <DetailItem label={t('global_asset_id', 'Global Asset ID')} value={assetInformation.globalAssetId} code />
                </div>
              )}
              {assetInformation.assetType && (
                <div className="col-12 col-sm-6 mb-2 pe-sm-3">
                  <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#999', marginBottom: '5px' }}>
                    {t('asset_type', 'Asset Type')}
                  </div>
                  <Badge bg="" style={{ backgroundColor: mainColor, fontSize: '12px', fontWeight: 500 }}>
                    {assetInformation.assetType}
                  </Badge>
                </div>
              )}
              {assetInformation.assetKind && (
                <div className="col-12 col-sm-6 mb-2">
                  <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#999', marginBottom: '5px' }}>
                    {t('asset_kind', 'Asset Kind')}
                  </div>
                  <Badge bg="light" text="dark" style={{ fontSize: '12px', fontWeight: 500, border: '1px solid #dee2e6' }}>
                    {assetInformation.assetKind}
                  </Badge>
                </div>
              )}
            </div>

            {/* Specific Asset IDs */}
            {specificAssetIds.length > 0 && (
              <>
                <SectionLabel>{t('specific_asset_ids', 'Specific Asset IDs')}</SectionLabel>
                <div className="d-flex flex-column gap-2 mb-2">
                  {specificAssetIds.map((specificAsset, index) => (
                    <div key={index} style={{ background: '#f8f9fa', padding: '8px 12px', borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#999', marginBottom: '2px' }}>
                        {specificAsset.name}
                      </div>
                      <code style={{ fontSize: '12px', color: '#555', wordBreak: 'break-all' }}>
                        {specificAsset.value}
                      </code>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* Data Specifications */}
        {embeddedDataSpecifications.length > 0 && (
          <>
            <hr style={{ borderColor: '#f0f0f0', margin: '4px 0 12px' }} />
            <SectionLabel>{t('data_specifications', 'Data Specifications')}</SectionLabel>
            <div className="d-flex flex-column gap-2 mb-2">
              {embeddedDataSpecifications.map((dataSpec, index) => {
                const names = dataSpec?.dataSpecificationContent?.preferredName ?? [];
                const nameEn = names.find(p => p.language === 'en')?.text;
                const nameDe = names.find(p => p.language === 'de')?.text;
                const name = nameEn || nameDe || names[0]?.text || '—';
                return (
                  <div key={index} style={{ background: '#f8f9fa', padding: '8px 12px', borderRadius: '8px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: '#333' }}>{name}</div>
                    {nameEn && nameDe && nameEn !== nameDe && (
                      <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>{nameDe}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Extensions */}
        {extensions.length > 0 && (
          <>
            <hr style={{ borderColor: '#f0f0f0', margin: '4px 0 12px' }} />
            <SectionLabel>{t('extension', 'Extension')}</SectionLabel>
            <div className="d-flex flex-column gap-2">
              {extensions.map((extension, index) => (
                <div key={index} style={{ background: '#f8f9fa', padding: '6px 10px', borderRadius: '8px' }}>
                  <code style={{ fontSize: '12px', color: '#555' }}>{extension?.name}</code>
                </div>
              ))}
            </div>
          </>
        )}

      </div>
    </DppModal>
  );
};

export default AasDetailsModal;
